import {
  DealEntity,
  DealPartyRelation,
  DealPartyRelationType,
  MultiPartyDealDetail,
} from './deal.types';
import { subDays, addDays } from 'date-fns';
import { dateToYmdStr } from '@lib/common';
import { $inany, $bw, $similar } from 'pages/deals/search/filters';
import { deliHistory } from '@global';

export function getRelatedFilter(deal: Partial<MultiPartyDealDetail>) {
  const published_date = new Date(deal.published_date);
  const filters: {
    id: string;
    value: {
      [x: string]: any;
    };
  }[] = [{ id: 'buyers', value: { [$inany.key]: (deal.buyers || []).map(b => b.name).join(',') } }];

  if (dealHas('seller', deal as MultiPartyDealDetail)) {
    filters.push({
      id: 'sellers',
      value: { [$inany.key]: (deal.sellers || []).map(s => s.name).join(',') },
    });
  }

  if (dealHas('content', deal as MultiPartyDealDetail)) {
    filters.push({
      id: 'contents',
      value: { [$inany.key]: (deal.contents || []).map(c => c.name).join(',') },
    });
  }

  filters.push({
    id: 'published_date',
    value: {
      [$bw.key]: [
        dateToYmdStr(subDays(published_date, 7)),
        dateToYmdStr(addDays(published_date, 7)),
      ],
    },
  });

  return filters;
}

// for similar
export function getSimilarDeal(deal: Partial<MultiPartyDealDetail>) {
  const published_date = new Date(deal.published_date);
  const filters: {
    id: string;
    value: {
      [x: string]: any;
    };
  }[] = [
    {
      id: 'similar',
      value: {
        [$similar.key]:
          // Pick only the first one until proper requirements are met.
          ((dealHas('buyer', deal as MultiPartyDealDetail) && deal.buyers[0].name) || '') +
          ':::' +
          ((dealHas('seller', deal as MultiPartyDealDetail) && deal.sellers[0].name) || '') +
          ':::' +
          ((dealHas('content', deal as MultiPartyDealDetail) && deal.contents[0].name) || '') +
          ':::' +
          dateToYmdStr(subDays(published_date, 7)) +
          ':::' +
          dateToYmdStr(addDays(published_date, 7)),
      },
    },
  ];

  return filters;
}

export function createReturnParam(prefix = '/deals/search') {
  const location = deliHistory.location;
  return location.search
    ? `?${new URLSearchParams({
        returnTo: `${prefix}${location.search}`,
      })}`
    : '';
}

/**
 * Check if the given deal is a single party deal.
 * @param deal The deal to check.
 */
export function isSinglePartyDeal(deal: MultiPartyDealDetail) {
  if (!deal.buyers || !(deal.contents || deal.sellers)) {
    return false;
  }

  let isSingleParty = deal.buyers.length === 1;

  if (isSingleParty && deal.contents && deal.contents.length >= 1) {
    isSingleParty = deal.contents.length === 1;
  }

  if (isSingleParty && deal.sellers && deal.sellers.length >= 1) {
    isSingleParty = deal.sellers.length === 1;
  }

  return isSingleParty;
}

/**
 * Check if the given multiparty deal has atleast one of the given enitity.
 *
 * ### Example
 * ```tsx
 * const hasSeller = dealHas('seller', deal); // Atleast one seller.
 * const hasBuyer = dealHas('buyer', deal); // Atleast one buyer.
 * const hasContent = dealHas('content', deal); // Atleast one content.
 * ```
 * @param toCheck Check if the given information exists
 */
export function dealHas(toCheck: DealPartyRelation, deal: MultiPartyDealDetail) {
  switch (toCheck) {
    case DealPartyRelationType.buyer:
      return deal?.buyers?.length >= 1;
    case DealPartyRelationType.seller:
      return deal?.sellers?.length >= 1;
    case DealPartyRelationType.content:
      return deal?.contents?.length >= 1;
  }
  return false;
}

/**
 * Check if the given multiparty deal has atleast one of the given enitity.
 *
 * ### Example
 * ```tsx
 * const hasSeller = dealHasOne('seller', deal); // Has exactly one seller.
 * const hasBuyer = dealHasOne('buyer', deal); // Has exactly one buyer.
 * const hasContent = dealHasOne('content', deal); // Has exactly one content.
 * ```
 * @param toCheck Check if the given information exists
 */
export function dealHasOne(toCheck: DealPartyRelation, deal: Partial<MultiPartyDealDetail>) {
  switch (toCheck) {
    case DealPartyRelationType.buyer:
      return deal?.buyers?.length === 1;
    case DealPartyRelationType.seller:
      return deal?.sellers?.length === 1;
    case DealPartyRelationType.content:
      return deal?.contents?.length === 1;
  }
  return false;
}

export function getContentNameType(entity: Partial<DealEntity>) {
  if (entity.name) return null;
  if (entity.working_title) return 'WT';
  if (entity.book_name) return 'BN';
  return null;
}

export function getEntityOrContentName(entity: Partial<DealEntity>) {
  return entity.name || entity.working_title || entity.book_name;
}
